import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { useSelector } from 'react-redux';
import { Box, Paper } from '@mui/material';

import { SearchInputRef } from './SearchInput';
import { RootState, SearchGroup, Suggestion } from 'types/types';

interface SearchSuggestionsProps {
  searchInputRef: React.RefObject<SearchInputRef>;
  handleSuggestionSelect: (clickedSuggestion: Suggestion) => void;
}

export interface SearchSuggestionsRef {
  setSuggestions: React.Dispatch<React.SetStateAction<SearchGroup | null>>;
}

const SearchSuggestions: ForwardRefRenderFunction<
  SearchSuggestionsRef,
  SearchSuggestionsProps
> = ({ handleSuggestionSelect, searchInputRef }, ref) => {
  const [suggestions, setSuggestions] = useState<SearchGroup | null>(null);

  const toggleMenu = useSelector(
    (state: RootState) => state.applicationState.toggle
  );

  useImperativeHandle(ref, () => ({
    setSuggestions,
  }));

  const groupDisplayNames: Record<string, string> = {
    cells: 'Cells',
    sites: 'Sites',
    mapPlaces: 'Map Places',
    geoJsonFeatures: 'Uploaded Layers',
  };

  const hasNoResults =
    suggestions?.cells.length === 0 &&
    suggestions?.sites.length === 0 &&
    suggestions?.mapPlaces.length === 0 &&
    suggestions?.geoJsonFeatures.length === 0;

  return (
    <Box className={toggleMenu ? 'suggestions-open' : 'suggestions'}>
      {suggestions && searchInputRef.current?.isLoading && (
        <Paper>
          <Box className="suggestions-container">
            {Object.keys(suggestions).map(
                (key) =>
                  suggestions[key].length > 0 && (
                    <div key={key}>
                      <Box className="suggestion-header">
                        {groupDisplayNames[key] ?? key.charAt(0).toUpperCase() + key.slice(1)}
                      </Box>
                      <Box className="suggestion-items-container">
                        {suggestions[key]?.map((suggestion: Suggestion) => (
                          <Box
                            className="suggestion-item-container"
                            key={suggestion.label}
                            onClick={() => {
                              handleSuggestionSelect(suggestion);
                              searchInputRef.current?.clearSearchInput();
                            }}
                          >
                            <Box className="suggestion-title">
                              {suggestion.label}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </div>
                  )
              )}
            {hasNoResults && (
              <Box className="search-no-result-container">No Results Found</Box>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default forwardRef<SearchSuggestionsRef, SearchSuggestionsProps>(
  SearchSuggestions
);
